import React, {useState} from 'react';

import cs from 'classnames';

import StateButton from 'app/general/widgets/StateButton';
import {useGeneratePolicyContent} from 'app/policies/api';
import {useGenerateProcedureContent} from 'app/procedures/api';
import {useSwitch} from 'app/settings/hooks';
import {gt} from 'app/utils/i18n';

import {DocumentAlertBox} from './DocumentAlertBox';

import type {BaseDocumentContentType, UserJobSerializedType} from 'sweet-types';

interface Props {
  url: string;
  contentType: BaseDocumentContentType;
  initialPrompt: string;
  generationAllowed: boolean;
  onGeneration: (promise: Promise<UserJobSerializedType>) => Promise<unknown>;
}
export function GenerateDocumentContentAvailable({
  url,
  initialPrompt,
  onGeneration,
  generationAllowed,
  contentType,
}: Props) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const openai_generation_switch = useSwitch('openai_generation');

  if (!openai_generation_switch) {
    return null;
  }

  const [generateProcedureContent] = useGenerateProcedureContent();
  const [generatePolicyContent] = useGeneratePolicyContent();

  const generatorFunction =
    contentType === 'procedure'
      ? generateProcedureContent
      : generatePolicyContent;

  if (!generationAllowed) {
    return null;
  }
  return (
    <DocumentAlertBox
      type="info"
      icon="fa-arrow-square-up"
      title={gt('Have SweetAI write this document')}
      content={
        <div>
          <div className="m-2">
            <p>
              {gt(
                'Use Sweet Artificial Intelligence to write this ' +
                  'document based on its title (or a different prompt).'
              )}
            </p>
            <div>
              <small>
                <ol>
                  <li>{gt('This may take a minute to complete.')}</li>
                </ol>
              </small>
            </div>
          </div>
          <input
            className="form-control form-control-sm m-2"
            name="description"
            autoFocus={true}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            type="text"
            placeholder={gt(
              'Write the title of a %s and we will write the content.',
              contentType
            )}
          />
          <StateButton
            className={cs('m-2 btn-info btn btn-sm text-nowrap')}
            onClick={() =>
              onGeneration(
                generatorFunction({
                  url,
                  prompt,
                }).unwrap()
              )
            }
            busyText={gt('Writing %s...', contentType)}
          >
            {gt('Write %s', contentType)}
          </StateButton>
        </div>
      }
    />
  );
}
